/**
 * The last ten hits.
 *
 * Not a realtime dashboard. Its whole job is the minute after somebody pastes
 * the snippet into their site: either their own visit shows up here or the
 * install is wrong, and without it the answer to "did it work" is "wait until
 * tomorrow".
 *
 * So it polls, and it is the only screen in the panel that does.
 */

import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { brand, settings } from "~/core/paths";
import { keys } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { deviceLabel, engineLabel, SourceNote, Unavailable } from "~/modules/analytics/pages/tracking/shared";

/** Often enough to feel live while somebody watches, rare enough to leave on. */
const POLL_MS = 10_000;

export function LivePage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "traffic", "live"],
		queryFn: () => api.trafficLive(entityId),
		refetchInterval: POLL_MS,
		// Not the shared `read` options: those keep a reading still while it is
		// looked at, and this one exists to move.
		staleTime: 0,
	});

	return (
		<StageShell
			entityId={entityId}
			reading="live"
			lede={t("live.lede")}
			filters={filters}
			setFilters={setFilters}
			actions={
				<Button variant="outline" disabled={state.isFetching} onClick={() => void state.refetch()}>
					{t("traffic.refresh")}
				</Button>
			}
		>
			<Async state={state}>
				{(data) => (
					<>
						<SourceNote frame={data} />
						{!data.available ? (
							<Unavailable frame={data} entityId={entityId} />
						) : (
							<Section
								action={
									<Link className="drill" to={`${brand(entityId)}/reflow/traffic`}>
										{t("reading.traffic")}
									</Link>
								}
								title={t("live.recent")}
								lede={t("live.recentHint")}
							>
								<p className="hint">{t("live.polling")}</p>
								{data.hits.length === 0 ? (
									<div className="stack py-(--s-8)">
										<h2>{t("live.waiting")}</h2>
										<p className="muted">{t("live.testSteps")}</p>
										<p className="hint">{t("live.troubleshoot")}</p>
										<div>
											<Link className="drill" to={settings(entityId, "tracking")}>
												{t("traffic.noSourceAction")}
											</Link>
										</div>
									</div>
								) : (
									<div className="table-scroll">
										<Table variant="resource">
											<TableHeader>
												<TableRow>
													<TableHead className="wrap">{t("live.when")}</TableHead>
													<TableHead className="wrap">{t("vitals.path")}</TableHead>
													<TableHead className="wrap">{t("traffic.engine")}</TableHead>
													{/* One column: five made the table wider than a
													    laptop and pushed the device out of view, and
													    neither half is read on its own here. */}
													<TableHead className="wrap">{t("live.where")}</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{data.hits.map((hit) => (
													<TableRow key={`${hit.at}${hit.path}`}>
														<TableCell className="wrap">
															{fmt.relative(locale, hit.at) ?? fmt.shortDateTime(locale, hit.at)}
														</TableCell>
														<TableCell className="wrap mono">{hit.path}</TableCell>
														<TableCell className="wrap">{engineLabel(t, hit.engine ?? "")}</TableCell>
														<TableCell className="wrap">
															{[
																hit.country ? fmt.region(locale, hit.country) : null,
																hit.device ? deviceLabel(t, hit.device) : null,
															]
																.filter(Boolean)
																.join(" · ") || "—"}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								)}
							</Section>
						)}
					</>
				)}
			</Async>
		</StageShell>
	);
}
